// FormalGrammarSystem.ts: Mathematical formal grammar frameworks
// Implements Chomsky hierarchy for language generation and processing

// Level 3: Regular Grammars (Finite State Automata)
export interface FiniteStateAutomaton {
    name: string;
    states: Set<string>;
    alphabet: Set<string>;
    transitions: Map<string, Map<string, string>>; // state -> input -> next_state
    startState: string;
    acceptStates: Set<string>;
    epsilonTransitions?: Map<string, Set<string>>; // For epsilon-NFA
}

export interface RegularGrammar {
    name: string;
    startSymbol: string;
    productions: Map<string, string[]>; // non-terminal -> terminal* (non-terminal)?
}

// Level 2: Context-Free Grammars
export interface ContextFreeGrammar {
    name: string;
    nonTerminals: Set<string>;
    terminals: Set<string>;
    productions: Map<string, string[][]>; // non-terminal -> alternative expansions
    startSymbol: string;
}

export interface ParseTreeNode {
    symbol: string;
    isTerminal: boolean;
    children: ParseTreeNode[];
    startIndex: number;
    endIndex: number;
}

export interface ParseResult {
    success: boolean;
    tree?: ParseTreeNode;
    error?: string;
}

// Level 1: Context-Sensitive Grammars
export interface ContextSensitiveRule {
    name: string;
    leftContext: string;      // Optional left context
    target: string;            // Target to replace
    rightContext: string;      // Optional right context
    replacement: string;       // Replacement string
    environment: string;       // Full environment description
}

export interface ContextSensitiveGrammar {
    name: string;
    startSymbol: string;
    vocabulary: Set<string>;
    rules: ContextSensitiveRule[];
}

// Level 0: Unrestricted Grammars (Type-0)
export interface UnrestrictedRule {
    name: string;
    leftSide: string;         // Sequence to match (tokenized by whitespace)
    rightSide: string;        // Replacement sequence
    weight?: number;          // Optional probabilistic preference
}

export interface UnrestrictedGrammar {
    name: string;
    startSymbol: string;
    rules: UnrestrictedRule[];
}

export class FormalGrammarSystem {
    private regularGrammars: Map<string, RegularGrammar> = new Map();
    private contextFreeGrammars: Map<string, ContextFreeGrammar> = new Map();
    private contextSensitiveGrammars: Map<string, ContextSensitiveGrammar> = new Map();
    private unrestrictedGrammars: Map<string, UnrestrictedGrammar> = new Map();
    private automata: Map<string, FiniteStateAutomaton> = new Map();

    constructor() {
        this.initializeDefaultGrammars();
    }

    private initializeDefaultGrammars(): void {
        // Initialize phonological regular grammar (assimilation, deletion, etc.)
        this.createPhonologicalRegularGrammar();
        
        // Initialize basic syntactic context-free grammar
        this.createBasicSyntacticGrammar();
        
        // Initialize morphological context-sensitive grammar
        this.createMorphologicalGrammar();

        // Initialize unrestricted transformational grammar
        this.createTransformationalGrammar();
    }

    // Regular Grammar: Phonological Processes (Finite State)
    private createPhonologicalRegularGrammar(): void {
        const grammar: RegularGrammar = {
            name: 'phonological_processes',
            startSymbol: 'S',
            productions: new Map([
                ['S', ['aA', 'eA', 'iA', 'oA', 'uA']],
                ['A', ['bA', 'cA', 'dA', 'fA', 'gA', 'hA', '#']],  // # = word boundary
            ])
        };
        
        this.regularGrammars.set('phonological_processes', grammar);
    }

    // Context-Free Grammar: Basic Syntax
    private createBasicSyntacticGrammar(): void {
        const grammar: ContextFreeGrammar = {
            name: 'basic_syntax',
            nonTerminals: new Set(['S', 'NP', 'VP', 'N', 'V', 'A', 'Det']),
            terminals: new Set(['essence', 'flows', 'being', 'transcends', 'the', 'pure']),
            productions: new Map([
                ['S', [['NP', 'VP']]],
                ['NP', [['Det', 'N'], ['N'], ['A', 'N']]],
                ['VP', [['V'], ['V', 'NP'], ['V', 'A']]],
                ['N', [['essence'], ['being']]],
                ['V', [['flows'], ['transcends']]],
                ['A', [['pure']]],
                ['Det', [['the']]]
            ]),
            startSymbol: 'S'
        };
        
        this.contextFreeGrammars.set('basic_syntax', grammar);
    }

    // Context-Sensitive Grammar: Morphological Processes
    private createMorphologicalGrammar(): void {
        const grammar: ContextSensitiveGrammar = {
            name: 'morphological_processes',
            startSymbol: 'ROOT',
            vocabulary: new Set(['essence', 'transcend', 'flow', 'pure', '+ed', '+ing', '+s']),
            rules: [
                {
                    name: 'past_tense',
                    leftContext: '',
                    target: 'transcend',
                    rightContext: '#',
                    replacement: 'transcended',
                    environment: 'word_final'
                },
                {
                    name: 'progressive',
                    leftContext: '',
                    target: 'flow',
                    rightContext: '#',
                    replacement: 'flowing',
                    environment: 'word_final'
                },
                {
                    name: 'plural',
                    leftContext: 'essence',
                    target: '#',
                    rightContext: '',
                    replacement: '+s',
                    environment: 'after_noun'
                }
            ]
        };
        
        this.contextSensitiveGrammars.set('morphological_processes', grammar);
    }

    // Unrestricted Grammar: transformational derivations
    private createTransformationalGrammar(): void {
        const grammar: UnrestrictedGrammar = {
            name: 'transformational_processes',
            startSymbol: 'S',
            rules: [
                { name: 'expand_sentence', leftSide: 'S', rightSide: 'NP VP', weight: 1.0 },
                { name: 'expand_np_det', leftSide: 'NP', rightSide: 'Det N', weight: 0.8 },
                { name: 'expand_np_bare', leftSide: 'NP', rightSide: 'N', weight: 0.2 },
                { name: 'expand_vp_transitive', leftSide: 'VP', rightSide: 'V NP', weight: 0.6 },
                { name: 'expand_vp_intransitive', leftSide: 'VP', rightSide: 'V', weight: 0.4 },
                { name: 'det_terminal', leftSide: 'Det', rightSide: 'the', weight: 1.0 },
                { name: 'n_terminal_essence', leftSide: 'N', rightSide: 'essence', weight: 0.5 },
                { name: 'n_terminal_being', leftSide: 'N', rightSide: 'being', weight: 0.5 },
                { name: 'v_terminal_flows', leftSide: 'V', rightSide: 'flows', weight: 0.5 },
                { name: 'v_terminal_transcends', leftSide: 'V', rightSide: 'transcends', weight: 0.5 },
                // Type-0 style rewrite that is not context-free: lexical reordering in specific context
                { name: 'reorder_transcends', leftSide: 'transcends the essence', rightSide: 'the essence transcends', weight: 0.1 }
            ]
        };

        this.unrestrictedGrammars.set('transformational_processes', grammar);
    }

    // Generate string from regular grammar
    public generateFromRegularGrammar(grammarName: string, maxLength: number = 10): string[] {
        const grammar = this.regularGrammars.get(grammarName);
        if (!grammar) return [];

        const results: string[] = [];
        const productions = grammar.productions.get(grammar.startSymbol) || [];
        
        const generate = (symbol: string, depth: number): string[] => {
            if (depth > maxLength) return [];
            
            const prod = grammar.productions.get(symbol);
            if (!prod) return [symbol];
            
            const results: string[] = [];
            for (const alternative of prod) {
                const parts: string[][] = [];
                for (const token of alternative) {
                    const generated = generate(token, depth + 1);
                    if (generated.length === 0) {
                        parts.push([token]);
                    } else {
                        parts.push(generated);
                    }
                }
                
                // Cross product of all parts
                const combinations = this.cartesianProduct(parts);
                for (const combination of combinations) {
                    let result = combination.join('');
                    // Remove word boundaries
                    result = result.replace(/#/g, '');
                    if (result.length > 0) {
                        results.push(result);
                    }
                }
            }
            
            return results;
        };
        
        return generate(grammar.startSymbol, 0).slice(0, 20);
    }

    private cartesianProduct(arrays: string[][]): string[][] {
        return arrays.reduce((acc: string[][], arr: string[]) => {
            const result: string[][] = [];
            acc.forEach(x => {
                arr.forEach(y => {
                    result.push([...x, y]);
                });
            });
            return result;
        }, [[]] as string[][]);
    }

    // Parse string with context-free grammar (simple top-down parser)
    public parseContextFree(grammarName: string, input: string): ParseResult {
        const grammar = this.contextFreeGrammars.get(grammarName);
        if (!grammar) {
            return { success: false, error: 'Grammar not found' };
        }

        const tokens = input.toLowerCase().split(/\s+/);
        
        return this.parseCFG(grammar, grammar.startSymbol, tokens, 0);
    }

    private parseCFG(grammar: ContextFreeGrammar, symbol: string, tokens: string[], index: number): ParseResult {
        // If it's a terminal symbol
        if (grammar.terminals.has(symbol)) {
            if (index < tokens.length && tokens[index] === symbol) {
                const node: ParseTreeNode = {
                    symbol,
                    isTerminal: true,
                    children: [],
                    startIndex: index,
                    endIndex: index + 1
                };
                return { success: true, tree: node };
            }
            return { success: false, error: `Expected terminal ${symbol}, got ${tokens[index]}` };
        }
        
        // If it's a non-terminal symbol
        const alternatives = grammar.productions.get(symbol);
        if (!alternatives) {
            return { success: false, error: `No production for ${symbol}` };
        }
        
        // Try each alternative
        for (const alternative of alternatives) {
            const result = this.parseAlternative(grammar, alternative, tokens, index);
            if (result.success && result.tree) {
                const node: ParseTreeNode = {
                    symbol,
                    isTerminal: false,
                    children: [result.tree],
                    startIndex: index,
                    endIndex: result.tree.endIndex
                };
                return { success: true, tree: node };
            }
        }
        
        return { success: false, error: `Failed to parse ${symbol}` };
    }

    private parseAlternative(grammar: ContextFreeGrammar, alternative: string[], tokens: string[], index: number): ParseResult {
        let currentIndex = index;
        const children: ParseTreeNode[] = [];
        
        for (const symbol of alternative) {
            const result = this.parseCFG(grammar, symbol, tokens, currentIndex);
            if (!result.success) {
                return result;
            }
            
            if (result.tree) {
                children.push(result.tree);
                currentIndex = result.tree.endIndex;
            }
        }
        
        // Create a virtual node for the alternative
        const node: ParseTreeNode = {
            symbol: alternative.join(' '),
            isTerminal: false,
            children,
            startIndex: index,
            endIndex: currentIndex
        };
        
        return { success: true, tree: node };
    }

    // Apply context-sensitive rules
    public applyContextSensitiveRules(grammarName: string, input: string): string {
        const grammar = this.contextSensitiveGrammars.get(grammarName);
        if (!grammar) return input;

        let result = input;
        
        for (const rule of grammar.rules) {
            const pattern = this.buildPattern(rule);
            const regex = new RegExp(pattern, 'g');
            
            result = result.replace(regex, (match, ...groups) => {
                const fullMatch = match;
                return fullMatch.replace(rule.target, rule.replacement);
            });
        }
        
        return result;
    }

    // Generate strings using unrestricted grammar with bounded derivation depth
    public generateFromUnrestrictedGrammar(
        grammarName: string,
        maxSteps: number = 8,
        maxLength: number = 12,
        maxOutputs: number = 20
    ): string[] {
        const grammar = this.unrestrictedGrammars.get(grammarName);
        if (!grammar) return [];

        const visited = new Set<string>();
        let frontier = new Set<string>([grammar.startSymbol]);
        visited.add(grammar.startSymbol);

        const outputs: string[] = [];

        for (let step = 0; step < maxSteps; step++) {
            const nextFrontier = new Set<string>();

            for (const sequence of frontier) {
                if (this.isTerminalSequence(sequence)) {
                    outputs.push(sequence);
                    if (outputs.length >= maxOutputs) {
                        return outputs;
                    }
                    continue;
                }

                const rewritten = this.applyUnrestrictedRewriteStep(sequence, grammar.rules, maxLength);
                for (const candidate of rewritten) {
                    if (!visited.has(candidate)) {
                        visited.add(candidate);
                        nextFrontier.add(candidate);
                    }
                }
            }

            if (nextFrontier.size === 0) {
                break;
            }
            frontier = nextFrontier;
        }

        return outputs;
    }

    // Apply unrestricted rules to an existing sequence and return reachable variants
    public applyUnrestrictedRules(
        grammarName: string,
        input: string,
        maxSteps: number = 4,
        maxLength: number = 20,
        maxOutputs: number = 20
    ): string[] {
        const grammar = this.unrestrictedGrammars.get(grammarName);
        if (!grammar) return [input];

        const normalizedInput = this.normalizeSequence(input);
        if (!normalizedInput) return [''];

        const visited = new Set<string>([normalizedInput]);
        let frontier = new Set<string>([normalizedInput]);

        for (let step = 0; step < maxSteps; step++) {
            const nextFrontier = new Set<string>();
            for (const sequence of frontier) {
                for (const candidate of this.applyUnrestrictedRewriteStep(sequence, grammar.rules, maxLength)) {
                    if (!visited.has(candidate)) {
                        visited.add(candidate);
                        nextFrontier.add(candidate);
                    }
                }
            }

            if (nextFrontier.size === 0) {
                break;
            }
            frontier = nextFrontier;

            if (visited.size >= maxOutputs) {
                break;
            }
        }

        return Array.from(visited).slice(0, maxOutputs);
    }

    private applyUnrestrictedRewriteStep(
        sequence: string,
        rules: UnrestrictedRule[],
        maxLength: number
    ): Set<string> {
        const results = new Set<string>();
        const tokens = this.tokenizeSequence(sequence);

        for (const rule of rules) {
            const leftTokens = this.tokenizeSequence(rule.leftSide);
            const rightTokens = this.tokenizeSequence(rule.rightSide);

            if (leftTokens.length === 0) {
                continue;
            }

            for (let i = 0; i <= tokens.length - leftTokens.length; i++) {
                let matches = true;
                for (let j = 0; j < leftTokens.length; j++) {
                    if (tokens[i + j] !== leftTokens[j]) {
                        matches = false;
                        break;
                    }
                }

                if (!matches) {
                    continue;
                }

                const candidateTokens = [
                    ...tokens.slice(0, i),
                    ...rightTokens,
                    ...tokens.slice(i + leftTokens.length)
                ];

                if (candidateTokens.length > maxLength) {
                    continue;
                }

                const candidate = this.normalizeSequence(candidateTokens.join(' '));
                if (candidate.length > 0) {
                    results.add(candidate);
                }
            }
        }

        return results;
    }

    private tokenizeSequence(sequence: string): string[] {
        return sequence
            .trim()
            .split(/\s+/)
            .filter(token => token.length > 0);
    }

    private normalizeSequence(sequence: string): string {
        return this.tokenizeSequence(sequence).join(' ');
    }

    private isTerminalSequence(sequence: string): boolean {
        const tokens = this.tokenizeSequence(sequence);
        return tokens.length > 0 && tokens.every(token => token.toLowerCase() === token);
    }

    private buildPattern(rule: ContextSensitiveRule): string {
        let pattern = '';
        
        if (rule.leftContext) {
            pattern += this.escapeRegex(rule.leftContext);
        }
        
        pattern += `(${this.escapeRegex(rule.target)})`;
        
        if (rule.rightContext) {
            pattern += this.escapeRegex(rule.rightContext);
        }
        
        return pattern;
    }

    private escapeRegex(str: string): string {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    // Check if finite state automaton accepts a string
    public automatonAccepts(automatonName: string, input: string): boolean {
        const automaton = this.automata.get(automatonName);
        if (!automaton) return false;

        let currentState = automaton.startState;
        
        for (const char of input) {
            const transitions = automaton.transitions.get(currentState);
            if (!transitions) return false;
            
            const nextState = transitions.get(char);
            if (!nextState) return false;
            
            currentState = nextState;
        }
        
        return automaton.acceptStates.has(currentState);
    }

    // Create a new finite state automaton for phonological processes
    public createPhonologicalAutomaton(): void {
        const automaton: FiniteStateAutomaton = {
            name: 'phonological_processes_automaton',
            states: new Set(['S0', 'S1', 'S2', 'S3', 'S4']),
            alphabet: new Set(['a', 'e', 'i', 'o', 'u', 'b', 'c', 'd', 'f', 'g', 'h', 'k', 'l', 'm', 'n', 'p', 'r', 's', 't', 'v', 'w', 'x', 'y', 'z']),
            transitions: new Map([
                ['S0', new Map([['a', 'S1'], ['e', 'S1'], ['i', 'S1'], ['o', 'S1'], ['u', 'S1']])],
                ['S1', new Map([
                    ['b', 'S2'], ['c', 'S2'], ['d', 'S2'], ['f', 'S2'], ['g', 'S2'],
                    ['h', 'S2'], ['k', 'S2'], ['l', 'S2'], ['m', 'S2'], ['n', 'S2'],
                    ['p', 'S2'], ['r', 'S2'], ['s', 'S2'], ['t', 'S2'], ['v', 'S2'],
                    ['w', 'S2'], ['x', 'S2'], ['y', 'S2'], ['z', 'S2']
                ])],
                ['S2', new Map([['a', 'S3'], ['e', 'S3'], ['i', 'S3'], ['o', 'S3'], ['u', 'S3']])],
                ['S3', new Map([
                    ['b', 'S2'], ['c', 'S2'], ['d', 'S2'], ['f', 'S2'], ['g', 'S2'],
                    ['h', 'S2'], ['k', 'S2'], ['l', 'S2'], ['m', 'S2'], ['n', 'S2'],
                    ['p', 'S2'], ['r', 'S2'], ['s', 'S2'], ['t', 'S2'], ['v', 'S2'],
                    ['w', 'S2'], ['x', 'S2'], ['y', 'S2'], ['z', 'S2']
                ])]
            ]),
            startState: 'S0',
            acceptStates: new Set(['S1', 'S3'])
        };
        
        this.automata.set('phonological_processes_automaton', automaton);
    }

    // Get all available grammars
    public getAvailableGrammars(): {
        regular: string[];
        contextFree: string[];
        contextSensitive: string[];
        unrestricted: string[];
        automata: string[];
    } {
        return {
            regular: Array.from(this.regularGrammars.keys()),
            contextFree: Array.from(this.contextFreeGrammars.keys()),
            contextSensitive: Array.from(this.contextSensitiveGrammars.keys()),
            unrestricted: Array.from(this.unrestrictedGrammars.keys()),
            automata: Array.from(this.automata.keys())
        };
    }
}

